/**
 * Chat interface - main RAG conversation UI with streaming support
 */

import React, { useState, useRef, useEffect } from 'react';
import { MessageSquare, Send, StopCircle, Brain, Search, Zap } from 'lucide-react';
import { Button } from '../UI/Button';
import { Card } from '../UI/Card';
import { MessageBubble } from './MessageBubble';
import { QueryModeSelector } from './QueryModeSelector';
import { useChatStore } from '../../stores/chat';
import { useStreamingRAG } from '../../hooks/useStreamingRAG';
import { useWorkspace } from '../../hooks/useWorkspace';

export function ChatInterface() {
  const [input, setInput] = useState('');
  const [useStreaming, setUseStreaming] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { projectId } = useWorkspace();
  const { messages, queryMode, addMessage, setLoading, isLoading } = useChatStore();
  
  // Streaming hook
  const streaming = useStreamingRAG();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, streaming.partialAnswer]);

  // When streaming completes, add the message
  useEffect(() => {
    if (streaming.stage === 'complete' && streaming.finalAnswer) {
      const assistantMessage = {
        id: Math.random().toString(36),
        role: 'assistant' as const,
        content: streaming.finalAnswer,
        citations: streaming.citations,
        timestamp: new Date(),
        confidence: undefined,
        mode: queryMode === 'auto' ? undefined : queryMode,
      };
      addMessage(assistantMessage);
      setLoading(false);
      streaming.reset();
    }
  }, [streaming.stage, streaming.finalAnswer]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!input.trim() || !projectId) return;

    const userMessage = {
      id: Math.random().toString(36),
      role: 'user' as const,
      content: input,
      timestamp: new Date(),
    };

    addMessage(userMessage);
    const question = input;
    setInput('');
    setLoading(true);

    if (useStreaming) {
      // Use streaming mode
      streaming.startQuery(projectId, {
        question,
        mode: queryMode,
        max_results: 10,
      });
    } else {
      // Fallback to non-streaming (import the old mutation if needed)
      // For now, streaming is the default
      streaming.startQuery(projectId, {
        question,
        mode: queryMode,
        max_results: 10,
      });
    }
  };

  const handleCancel = () => {
    streaming.cancelQuery();
    setLoading(false);
  };

  if (!projectId) {
    return (
      <Card>
        <div className="text-center py-12 text-gray-500">
          <MessageSquare className="h-12 w-12 mx-auto mb-4 text-gray-400" />
          <p>Please select a project to start chatting</p>
        </div>
      </Card>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <Card title="Evidence-Backed Chat" className="flex-1 flex flex-col">
        <div className="mb-4 flex items-center justify-between">
          <QueryModeSelector />
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={useStreaming}
              onChange={(e) => setUseStreaming(e.target.checked)}
              className="rounded border-gray-300"
            />
            <Zap className="h-4 w-4 text-yellow-500" />
            Stream
          </label>
        </div>

        <div className="flex-1 overflow-y-auto space-y-4 mb-4">
          {messages.length === 0 && !streaming.isStreaming ? (
            <div className="text-center py-12 text-gray-500">
              <MessageSquare className="h-12 w-12 mx-auto mb-4 text-gray-400" />
              <p>Ask a question about your documents</p>
              <p className="text-sm mt-2">All answers are backed by citations</p>
            </div>
          ) : (
            <>
              {messages.map((message) => (
                <MessageBubble key={message.id} message={message} />
              ))}
              
              {/* Streaming response */}
              {streaming.isStreaming && (
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4 border border-indigo-200">
                  {/* Status indicator */}
                  <div className="flex items-center gap-2 mb-3">
                    {streaming.stage === 'retrieval' || streaming.stage === 'started' || streaming.stage === 'context' ? (
                      <Search className="h-4 w-4 text-indigo-600 animate-pulse" />
                    ) : streaming.stage === 'thinking' ? (
                      <Brain className="h-4 w-4 text-purple-600 animate-pulse" />
                    ) : (
                      <Zap className="h-4 w-4 text-yellow-600 animate-pulse" />
                    )}
                    <span className="text-sm font-medium text-indigo-800">
                      {streaming.message}
                    </span>
                    {streaming.elapsed > 0 && (
                      <span className="text-xs text-indigo-600 ml-auto">
                        {streaming.elapsed.toFixed(1)}s
                      </span>
                    )}
                  </div>
                  
                  {/* Progress bar */}
                  <div className="h-1 bg-indigo-100 rounded-full mb-3 overflow-hidden">
                    <div
                      className={`h-full transition-all duration-300 ${
                        streaming.stage === 'thinking' ? 'bg-purple-500' :
                        streaming.stage === 'generating' ? 'bg-green-500' :
                        'bg-indigo-500'
                      }`}
                      style={{
                        width: streaming.stage === 'retrieval' ? '30%' :
                               streaming.stage === 'thinking' ? '50%' :
                               streaming.stage === 'generating' ? `${Math.min(90, 50 + (streaming.partialAnswer.length / 50))}%` :
                               '100%',
                      }}
                    />
                  </div>
                  
                  {/* Partial answer */}
                  {streaming.partialAnswer && (
                    <div className="prose prose-sm max-w-none">
                      <p className="text-gray-800 whitespace-pre-wrap">
                        {streaming.partialAnswer}
                        <span className="inline-block w-2 h-4 bg-indigo-500 ml-1 animate-pulse" />
                      </p>
                    </div>
                  )}
                  
                  {/* Stats when thinking */}
                  {streaming.stage === 'thinking' && streaming.thinkingTime > 0 && (
                    <div className="mt-2 text-xs text-purple-600">
                      🧠 AI reasoning... {streaming.thinkingTime.toFixed(1)}s
                    </div>
                  )}
                  
                  {/* Evidence count */}
                  {streaming.evidenceCount > 0 && (
                    <div className="mt-2 text-xs text-indigo-600">
                      📚 Found {streaming.evidenceCount} evidence sources
                    </div>
                  )}
                </div>
              )}
            </>
          )}
          <div ref={messagesEndRef} />
        </div>

        <form onSubmit={handleSubmit} className="flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask a question..."
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            disabled={isLoading || streaming.isStreaming}
          />
          {streaming.isStreaming ? (
            <Button type="button" onClick={handleCancel} variant="secondary">
              <StopCircle className="h-5 w-5 text-red-500" />
            </Button>
          ) : (
            <Button type="submit" disabled={!input.trim() || isLoading} isLoading={isLoading}>
              <Send className="h-5 w-5" />
            </Button>
          )}
        </form>
      </Card>
    </div>
  );
}


